import { useEffect, useRef, useState } from "react";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { mensagemDaAbertura, type MotivoAbertura } from "../lib/abrir";
import {
  BUSCA_VAZIA,
  buscar,
  cnpjFormatado,
  ehFalha,
  listarUfs,
  POR_PAGINA,
  type Busca,
  type Resultado,
  type Sindicato,
} from "../lib/sindicatos";

/**
 * O SindiFácil (000212): "qual sindicato representa esta categoria neste
 * estado?" — a busca nas 18.419 entidades do cadastro sindical.
 *
 * ## Por que a busca é do servidor
 *
 * A irmã dela, a busca de ocupações do MEI, manda as 502 linhas de uma vez e
 * filtra no navegador. Aqui a conta é outra, e foi medida: o catálogo inteiro
 * pesa **6,27 MB** em JSON enxuto, e só São Paulo pesa **1,05 MB**. Então a RPC
 * pagina, e a tela espera o resultado — com o `debounce` abaixo para não pedir
 * uma viagem por tecla digitada.
 *
 * ## O que a tela mostra, e por que é lista e não tabela
 *
 * A ferramenta do Bubble mostra uma TABELA de quatro colunas (CNPJ, categoria,
 * denominação, UF), e a categoria é texto livre que vai a **8.324 caracteres** —
 * medido na base inteira: 4.694 linhas passam de 200 caracteres e 739 passam de
 * mil. Numa célula de tabela isso produz uma linha de centenas de pixels de
 * altura ao lado de três células quase vazias, e a página inteira vira uma
 * parede — é a cicatriz do IR Pro (8.249px para o mesmo conteúdo) e a da busca
 * de CNAE do Radar (8.521px, 50 cartões).
 *
 * Aqui cada entidade é uma LINHA de lista: o nome em destaque, o CNPJ e a UF
 * como fatos curtos, e a categoria — que é o texto que responde à pergunta —
 * cortada em {@link CORTE} caracteres com "ver completa" para as longas.
 *
 * ## O que a tela NÃO afirma
 *
 * Não achar nada não quer dizer que o sindicato não existe: quer dizer que ele
 * não está nesta lista, que é uma carga de fevereiro de 2025. Base sindical
 * muda por fusão, criação e alteração de base territorial, e uma lista de
 * referência sem idade visível é a que envelhece sem ninguém notar. O rodapé
 * diz os dois fatos.
 *
 * ## Sem estado na URL
 *
 * O que se compartilha é a FERRAMENTA, não a consulta — ninguém manda ao colega
 * "a busca de metalúrgicos em SP". É a mesma decisão da busca de ocupações; o
 * oposto do Mapa de Concursos, onde a UF e a situação viajam na query porque
 * "os abertos no Paraná" é exatamente o que se manda a alguém.
 */

/** Onde a categoria é cortada. Acima disto, a linha ganha "ver completa".
 *  Medido: p50 72 · p75 205 · p90 509 · máx 8.324 — então o corte alcança
 *  cerca de um quarto das linhas, e a maioria aparece inteira. */
const CORTE = 280;

/** A espera antes de pedir ao servidor. Curta o bastante para parecer imediata,
 *  longa o bastante para quem digita "metalúrgicos" gastar uma viagem e não
 *  treze. */
const ESPERA_MS = 350;

export default function SindiFacil() {
  const [busca, setBusca] = useState<Busca>(BUSCA_VAZIA);
  const [ufs, setUfs] = useState<string[] | null>(null);
  const [semUf, setSemUf] = useState(0);
  const [res, setRes] = useState<Resultado | null>(null);
  const [buscando, setBuscando] = useState(true);
  const [recusa, setRecusa] = useState<MotivoAbertura | null>(null);
  const [erro, setErro] = useState(false);

  // A chamada em voo. Sem isto, uma resposta lenta de uma busca ANTERIOR chega
  // depois da atual e sobrescreve o resultado certo pelo velho — o defeito é
  // invisível e intermitente, e aparece justamente com internet ruim.
  const rodada = useRef(0);

  useEffect(() => {
    void (async () => {
      const r = await listarUfs();
      if (r === null) setErro(true);
      else if (ehFalha(r)) setRecusa(r.motivo as MotivoAbertura);
      else {
        setUfs(r.ufs);
        setSemUf(r.semUf);
      }
    })();
  }, []);

  useEffect(() => {
    const minha = ++rodada.current;
    setBuscando(true);
    const t = setTimeout(() => {
      void (async () => {
        const r = await buscar(busca);
        if (minha !== rodada.current) return; // chegou tarde: a tela já pediu outra
        if (r === null) setErro(true);
        else if (ehFalha(r)) setRecusa(r.motivo as MotivoAbertura);
        else {
          setRes(r);
          setRecusa(null);
        }
        setBuscando(false);
      })();
    }, ESPERA_MS);
    return () => clearTimeout(t);
  }, [busca]);

  if (recusa) {
    const m = mensagemDaAbertura(recusa);
    return (
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="rounded-cf-lg border border-borda bg-superficie p-6">
          <h1 className="font-display text-xl font-bold text-titulo">{m.titulo}</h1>
          <p className="mt-2 text-sm leading-relaxed text-cinza">{m.texto}</p>
        </div>
      </main>
    );
  }

  if (erro) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <p className="rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar o cadastro sindical agora. Recarregue a página; se
          continuar, fale com o suporte.
        </p>
      </main>
    );
  }

  if (!res && buscando) return <Carregando />;

  const mudar = (parte: Partial<Busca>) =>
    // Trocar qualquer filtro volta à página 1: manter a página é como o aluno
    // digita um termo novo e recebe "nenhum resultado" estando na página 40.
    setBusca((b) => ({ ...b, ...parte, pagina: parte.pagina ?? 1 }));

  return (
    <main className="mx-auto max-w-5xl px-4 py-7 sm:px-6">
      <Cabine
        rotulo="Consulta"
        titulo="SindiFácil"
        descricao="Procure a entidade sindical pela categoria que ela representa, pelo nome ou pelo CNPJ. Filtre pelo estado para ver só a base de lá."
        rodape={
          <div className="grid gap-4 sm:grid-cols-[10rem_1fr] lg:grid-cols-[10rem_1fr_14rem]">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
                Estado
              </span>
              {/* O `text-white` aqui é do CAMPO, que é desenhado sobre o navy.
                  Quem cuida do MENU SUSPENSO — que a `<option>` herda e o
                  navegador pinta fora da página — é o TERRITÓRIO: as regras
                  `.painel-navy select` e `.painel-navy option` do `index.css`,
                  com a medição e as duas defesas explicadas lá. Não escreva cor
                  de `<option>` aqui: duas tentativas já falharam neste ponto —
                  `text-tinta` em cada opção (token semântico dentro do navy, que
                  a varredura do caso 13 pegou) e `[color-scheme:dark]` solto no
                  `select`, que Victor viu falhar na tela em 10/09. */}
              <select
                className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-3 text-base text-white focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
                id="filtro-uf"
                value={busca.uf}
                onChange={(e) => mudar({ uf: e.target.value })}
              >
                {/* O combo só oferece as siglas que EXISTEM na tabela — opção
                    que leva a lista vazia é ruído. E sem contagem ao lado: em
                    09/09 Victor mandou tirar os números dos combos do balcão
                    porque eles não se recalculavam sob os outros filtros. O
                    total aparece no resultado, onde é sempre verdade. */}
                <option value="">Todos</option>
                {(ufs ?? []).map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
                Categoria ou nome
              </span>
              <input
                className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-4 text-base text-white placeholder:text-navy-mute/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
                type="search"
                autoFocus
                id="filtro-termo"
                value={busca.termo}
                onChange={(e) => mudar({ termo: e.target.value })}
                placeholder="metalúrgicos, comerciários, professores…"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-bold tracking-widest text-navy-mute uppercase">
                CNPJ
              </span>
              <input
                className="h-12 w-full rounded-xl border border-navy-mute/30 bg-white/5 px-4 text-base tabular-nums text-white placeholder:text-navy-mute/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
                type="search"
                inputMode="numeric"
                id="filtro-cnpj"
                value={busca.cnpj}
                onChange={(e) => mudar({ cnpj: e.target.value })}
                placeholder="00.290.686/0001-00"
              />
            </label>
          </div>
        }
      />

      <Resultados
        res={res}
        buscando={buscando}
        filtrando={busca.uf !== "" || busca.termo.trim() !== "" || busca.cnpj.trim() !== ""}
        semUf={semUf}
        aoPaginar={(p) => setBusca((b) => ({ ...b, pagina: p }))}
        aoLimpar={() => setBusca(BUSCA_VAZIA)}
      />

      <p className="mt-8 rounded-xl bg-superficie-2 p-4 text-xs leading-relaxed text-cinza">
        Esta lista veio do sistema antigo, onde foi carregada em fevereiro de 2025.
        Base sindical muda — entidades se fundem, mudam de nome e alteram a base
        territorial —, então confirme a entidade e a vigência da convenção antes de
        aplicar em folha. Não achar aqui não significa que o sindicato não exista.
      </p>
    </main>
  );
}

function Resultados({
  res,
  buscando,
  filtrando,
  semUf,
  aoPaginar,
  aoLimpar,
}: {
  res: Resultado | null;
  buscando: boolean;
  filtrando: boolean;
  semUf: number;
  aoPaginar: (p: number) => void;
  aoLimpar: () => void;
}) {
  if (!res) return null;
  const { total, pagina, paginas, itens } = res;
  const de = (pagina - 1) * POR_PAGINA + 1;
  const ate = (pagina - 1) * POR_PAGINA + itens.length;

  return (
    <>
      {/* `aria-busy` em vez de trocar a lista por um spinner: quem está lendo o
          resultado anterior não perde o lugar enquanto a busca nova viaja. */}
      <div
        className="mt-6 flex flex-wrap items-center justify-between gap-3"
        aria-busy={buscando}
      >
        {/* `data-total` é escopo NOMEADO para a prova: ler o número do texto
            corrido exigiria desfazer o `toLocaleString`, e a prova passaria a
            medir a formatação em vez do valor. */}
        <p role="status" data-total={total} className="text-sm text-cinza">
          {total === 0
            ? "Nenhuma entidade encontrada."
            : `${total.toLocaleString("pt-BR")} ${total === 1 ? "entidade" : "entidades"}` +
              (paginas > 1 ? ` · mostrando ${de}–${ate}` : "")}
        </p>
        {filtrando && (
          <button type="button" onClick={aoLimpar} className="btn-contorno text-sm">
            Limpar filtros
          </button>
        )}
      </div>

      {total === 0 && (
        <div className="mt-3 rounded-xl bg-superficie-2 p-5 text-sm leading-relaxed text-cinza">
          <p className="font-semibold text-titulo">Não achar aqui não quer dizer que não exista.</p>
          <p className="mt-1">
            A busca casa com a categoria representada e com o nome da entidade. Tente
            uma palavra só (&ldquo;metalúrgic&rdquo; em vez de &ldquo;sindicato dos
            metalúrgicos&rdquo;), tire o filtro de estado, ou procure pelo CNPJ, que
            é o jeito mais certeiro.
          </p>
        </div>
      )}

      <ul className="mt-3 divide-y divide-borda overflow-hidden rounded-cf-lg border border-borda bg-superficie">
        {itens.map((s) => (
          <Linha key={s.id} s={s} />
        ))}
      </ul>

      {paginas > 1 && (
        <nav
          className="mt-4 flex items-center justify-center gap-3"
          aria-label="Páginas do resultado"
        >
          <button
            type="button"
            className="btn-contorno text-sm"
            data-anterior
            disabled={pagina <= 1}
            onClick={() => aoPaginar(pagina - 1)}
          >
            Anterior
          </button>
          <span className="text-sm tabular-nums text-cinza">
            {pagina} de {paginas.toLocaleString("pt-BR")}
          </span>
          <button
            type="button"
            className="btn-contorno text-sm"
            data-proxima
            disabled={pagina >= paginas}
            onClick={() => aoPaginar(pagina + 1)}
          >
            Próxima
          </button>
        </nav>
      )}

      {/* As 3 linhas sem UF só são alcançáveis sem filtro de estado. Dizer isso é
          melhor que deixá-las sumir em silêncio — e o aviso só aparece se elas
          existirem, porque zero grande é ruído. */}
      {semUf > 0 && (
        <p className="mt-4 text-center text-xs text-cinza">
          {semUf === 1
            ? "Uma entidade não tem o estado preenchido no cadastro de origem e só aparece sem o filtro de estado."
            : `${semUf} entidades não têm o estado preenchido no cadastro de origem e só aparecem sem o filtro de estado.`}
        </p>
      )}
    </>
  );
}

function Linha({ s }: { s: Sindicato }) {
  const [aberta, setAberta] = useState(false);
  const categoria = s.categoria ?? "";
  const longa = categoria.length > CORTE;
  const visivel = longa && !aberta ? `${categoria.slice(0, CORTE).trimEnd()}…` : categoria;

  return (
    <li data-linha={s.id} data-uf={s.uf ?? ""} className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p data-nome className="min-w-0 flex-1 font-semibold text-titulo">
          {/* Nula em 21 linhas da origem. Dizer que o cadastro não tem o nome é
              melhor que uma linha que começa com um espaço vazio — o vazio se
              lê como defeito da tela. */}
          {s.denominacao ?? (
            <span className="font-normal text-cinza italic">Entidade sem nome no cadastro</span>
          )}
        </p>
        {s.uf ? (
          <span className="shrink-0 rounded-full bg-chip px-2.5 py-0.5 text-xs font-bold text-acento">
            {s.uf}
          </span>
        ) : (
          <span className="shrink-0 rounded-full bg-chip px-2.5 py-0.5 text-xs text-cinza">
            estado não informado
          </span>
        )}
      </div>

      <p data-cnpj className="mt-1 text-xs tabular-nums text-cinza">
        CNPJ {cnpjFormatado(s.cnpj)}
      </p>

      {categoria !== "" ? (
        <p data-categoria data-longa={longa ? "sim" : "nao"} className="mt-1.5 text-sm leading-relaxed text-cinza">
          {visivel}{" "}
          {longa && (
            <button
              type="button"
              data-ver-completa
              onClick={() => setAberta((a) => !a)}
              className="font-semibold text-link underline underline-offset-2"
            >
              {aberta ? "ver menos" : "ver completa"}
            </button>
          )}
        </p>
      ) : (
        <p data-categoria data-longa="nao" className="mt-1.5 text-sm text-cinza italic">
          Categoria não informada no cadastro.
        </p>
      )}
    </li>
  );
}
